import { useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { Add } from "@mui/icons-material";
import {
  ColumnVisibilityFilter,
  SearchFilter,
  SectionHeader,
} from "@shared/components";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import {
  ColumnKey,
  FilterField,
  PublisherFormValues,
  PublisherHeaderProps,
} from "../utils/types";
import PublisherForm from "./PublisherForm";
import { ColumnOptions } from "../utils/constants";
import { FormActions } from "@shared/utils/constant";
import { useCreatePublisher } from "../services/useCreatePublisher";
import { Can } from "@core/casl-provider";

const filterFields: FilterField[] = [
  {
    id: "publisher-search",
    name: "searchQuery",
    label: "Search Publishers",
    placeholder: "Search publishers...",
    clearAriaLabel: "clear publisher search",
  },
];

const PublisherHeader = ({
  visibleColumns,
  setVisibleColumns,
}: PublisherHeaderProps) => {
  const [actionState, setActionState] = useState<{
    action: FormActions.ADD | "";
    payload: PublisherFormValues | object;
  }>({
    action: "",
    payload: {} as PublisherFormValues,
  });

  const { mutateAsync: createPublisherAsync, isPending } = useCreatePublisher();

  const clearActionState = () => {
    setActionState({ action: "", payload: {} });
  };
  const handleSubmit = async (
    values: PublisherFormValues,
    helpers: FormikHelpers<PublisherFormValues & { id?: number }>
  ) => {
    try {
      await createPublisherAsync({ payload: values });
      clearActionState();
    } catch (error) {
      const errMsg = (error as Error)?.message || "Something went wrong";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };
  return (
    <>
      <Box>
        <SectionHeader title="Publishers" />
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          mt={3}
          alignItems="center"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderBottom: 0,
            backgroundColor: "background.paper",
            padding: "15px",
            borderRadius: "4px 4px 0 0",
          })}
        >
          <SearchFilter filterFields={filterFields} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <ColumnVisibilityFilter
              value={visibleColumns as string[]}
              onChange={(vals) => setVisibleColumns(vals as ColumnKey[])}
              options={ColumnOptions}
              size="medium"
            />

            <Can
              do={"create"}
              on={"Publisher"}
            >
              <Button
                variant="contained"
                sx={{ height: 38, padding: "0 20px" }}
                onClick={() =>
                  setActionState({ action: FormActions.ADD, payload: {} })
                }
                startIcon={<Add />}
              >
                Add New Publisher
              </Button>
            </Can>
          </Stack>
        </Stack>
      </Box>

      <PublisherForm
        open={actionState.action === FormActions.ADD}
        onClose={clearActionState}
        title="Add Publisher"
        initialValues={actionState.payload}
        disable={isPending}
        onSubmit={handleSubmit}
        showCloseButton
      />
    </>
  );
};

export default PublisherHeader;
